import { inject, Injectable } from "@angular/core";
import { MCAuthoritiesEventsManufacturers, MCCustomFunctionsParams, MCDialog, MCDialogButton, MCDialogFooter, MCDialogHeader } from "@maslul/common-services";
import { ProductsService } from "../../../pages/products/products";
import { ProductsStoreService } from "../../../store/products";
import { FormisFormStoreData } from "../../../../models/store/formis-form-store-data";
import { TranslateService } from "@ngx-translate/core";
import { DialogService } from "../../../shared/dialog.service";

interface ProductManufacturerEntry {
    manufacturerName: any;
    originCountry: any;
}

interface ProductManufacturersData {
    guid: string;
    manufacturers: ProductManufacturerEntry[];
}

@Injectable({
    providedIn: 'root'
})
export class AuthoritiesEventsManufacturers extends MCAuthoritiesEventsManufacturers {

    productText: any;
    private productsService = inject(ProductsService);
    private productsStore = inject(ProductsStoreService);
    private translateService = inject(TranslateService);
    private dialogService = inject(DialogService);

    constructor() {
        super();
        this.productText = this.translateService.instant("products");
    }

    override checkDuplicateManufactureDetails(customFunctionsParams: MCCustomFunctionsParams): void {
        const currentProduct: FormisFormStoreData | undefined = this.productsService.getActiveProduct();
        const allProducts: Map<string, FormisFormStoreData> = this.productsStore.getSnapshot().products as Map<string, FormisFormStoreData>;
        const activeProductGuid: string = this.productsStore.getSnapshot().activeProduct || "";

        if (!currentProduct || !allProducts) return;

        const otherProductsManufacturers: ProductManufacturersData[] = this.getOtherProductsManufacturers(allProducts, activeProductGuid);

        const currentProductModel = customFunctionsParams.formisForm.model
        const currentProductManufacturers: ProductManufacturersData = {
            guid: activeProductGuid,
            manufacturers: this.extractManufacturersFromProduct(currentProductModel)
        };

        const isDuplicate = this.hasManufacturerInOtherProducts(currentProductManufacturers, otherProductsManufacturers);

        if (isDuplicate) {
            this.resetCurrentManufacturerFields(customFunctionsParams);
            this.showDuplicateManufacturerMessage();
        }
    }

    private resetCurrentManufacturerFields(customFunctionsParams: MCCustomFunctionsParams): void {
        const currentIndex = customFunctionsParams.index || 0;

        // עדכון המודל
        const manufacturerDetailsModel = customFunctionsParams.formisForm.model?.manufacturersDetails?.manufacturerDetails;
        if (manufacturerDetailsModel?.[currentIndex]) {
            manufacturerDetailsModel[currentIndex].manufacturerObject = '';
            manufacturerDetailsModel[currentIndex].originCountry = '';
        }

        // עדכון ה-formControl כדי שה-UI יתעדכן
        const manufacturerDetailsFields = customFunctionsParams.formisForm.fields
            ?.find(x => x.key === "manufacturersDetails")?.fieldGroup
            ?.find(x => x.key === "manufacturerDetails")?.fieldGroup;

        if (manufacturerDetailsFields?.[currentIndex]) {
            manufacturerDetailsFields[currentIndex]?.fieldGroup
                ?.find(field => field.key === "manufacturerObject")?.formControl?.setValue('', { emitEvent: false });
            manufacturerDetailsFields[currentIndex]?.fieldGroup
                ?.find(field => field.key === "originCountry")?.formControl?.setValue('', { emitEvent: false });
        }
    }


    private showDuplicateManufacturerMessage(): void {
        const dialogDuplicateManufactureText = this.productText.dialogDuplicateManufacture;

        const dialog = new MCDialog();
        dialog.visible = true;
        dialog.contentText = dialogDuplicateManufactureText?.text;

        dialog.header = new MCDialogHeader();
        dialog.header.closeButton = new MCDialogButton();
        dialog.header.closeButton.label = "X";
        dialog.header.closeButton.event = () => { dialog.visible = false; this.dialogService.dialog.set(dialog); };

        dialog.footer = new MCDialogFooter();
        dialog.footer.button = new MCDialogButton();
        dialog.footer.button.label = dialogDuplicateManufactureText?.okButton;
        dialog.footer.button.event = () => { dialog.visible = false; this.dialogService.dialog.set(dialog); };

        this.dialogService.dialog.set(dialog);
    }

    private hasManufacturerInOtherProducts(currentProductManufacturers: ProductManufacturersData, otherProductsManufacturers: ProductManufacturersData[]): boolean {
        const allOtherManufacturersList = otherProductsManufacturers.flatMap(product => product.manufacturers);

        const isDuplicateExists = currentProductManufacturers.manufacturers.some(currentManufacturer =>
            allOtherManufacturersList.some(otherManufacturer =>
                (!otherManufacturer.manufacturerName || otherManufacturer.manufacturerName === currentManufacturer.manufacturerName)
                &&
                (!otherManufacturer.originCountry || otherManufacturer.originCountry === currentManufacturer.originCountry)
            )
        );

        return isDuplicateExists;
    }

    private isNotCurrentProduct(guid: string, activeProductGuid: string): boolean {
        return guid !== activeProductGuid;
    }

    private extractManufacturersFromProduct(productModel: any): ProductManufacturerEntry[] {
        const manufacturerDetailsList = productModel?.manufacturersDetails?.manufacturerDetails || [];
        if (!Array.isArray(manufacturerDetailsList)) return [];

        const manufacturers = manufacturerDetailsList.map((entry: any) => ({
            manufacturerName: entry?.manufacturerObject?.value ?? entry?.manufacturerObject ?? null,
            originCountry: entry?.originCountry?.value ?? entry?.originCountry ?? null
        }));
        return manufacturers;
    }

    private getOtherProductsManufacturers(allProducts: Map<string, FormisFormStoreData>, activeProductGuid: string): ProductManufacturersData[] {
        const otherProductsManufacturers = Array.from(allProducts.entries())
            .filter(([guid]) => this.isNotCurrentProduct(guid, activeProductGuid))
            .map(([guid, product]) => ({
                guid,
                manufacturers: this.extractManufacturersFromProduct(product.formisFormModel)
            }));
        return otherProductsManufacturers;
    }
}
